import BrushIcon from "@mui/icons-material/Brush";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightModeIcon from "@mui/icons-material/LightMode";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import ShieldIcon from "@mui/icons-material/Shield";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ViewListIcon from "@mui/icons-material/ViewList";
import WorkIcon from "@mui/icons-material/Work";
import {
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useEffect, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { useLocation, useNavigate } from "react-router-dom";
import { InternSidebarBrandsQuery } from "./__generated__/InternSidebarBrandsQuery.graphql";
import KinternLogo from "../../../components/internal/KinternLogo";
import { useBrandWorkspace } from "../../../contexts/BrandWorkspaceContext";
import { useColorMode } from "../../../contexts/ColorModeContext";

const SIDEBAR_EXPANDED_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const SIDEBAR_COLLAPSE_KEY = "intern-sidebar-collapsed";

// Query to fetch brands for the selector
const brandsQuery = graphql`
  query InternSidebarBrandsQuery {
    internBrands {
      id
      displayName
    }
  }
`;

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  exact?: boolean;
  disabled?: boolean;
}

// Platform section navigation items
const PLATFORM_NAV_ITEMS: NavItem[] = [
  {
    name: "Overview",
    path: "/intern",
    icon: <DashboardIcon />,
    exact: true,
  },
  {
    name: "Jobs",
    path: "/intern/jobs",
    icon: <WorkIcon />,
  },
  {
    name: "All Brands",
    path: "/intern/brands",
    icon: <ViewListIcon />,
  },
  {
    name: "Privacy",
    path: "/intern/privacy",
    icon: <ShieldIcon />,
  },
];

// Brand workspace navigation items
const WORKSPACE_NAV_ITEMS: NavItem[] = [
  {
    name: "Dashboard",
    path: "/intern/workspace/overview",
    icon: <DashboardIcon />,
  },
  {
    name: "Billing",
    path: "/intern/workspace/billing",
    icon: <MonetizationOnIcon />,
  },
  {
    name: "Onboarding",
    path: "/intern/workspace/onboarding",
    icon: <PlayCircleOutlineIcon />,
  },
  {
    name: "Builds",
    path: "/intern/workspace/builds",
    icon: <BuildIcon />,
  },
  {
    name: "Jobs",
    path: "/intern/workspace/jobs",
    icon: <WorkIcon />,
  },
  {
    name: "Style",
    path: "/intern/workspace/style",
    icon: <BrushIcon />,
  },
  {
    name: "Content Gen",
    path: "/intern/workspace/generation",
    icon: <TextFieldsIcon />,
  },
];

// Helper function to check if nav item is active
const isNavItemActive = (item: NavItem, pathname: string): boolean => {
  if (item.exact) {
    return pathname === item.path || pathname === item.path + "/";
  }
  return pathname.startsWith(item.path);
};

interface InternSidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const InternSidebar = ({ mobileOpen, onMobileClose }: InternSidebarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, setColorMode, isSystemPreference } = useColorMode();
  const { selectedBrandId, selectedBrandName, setSelectedBrand } =
    useBrandWorkspace();

  // Fetch brands for selector
  const brandsData = useLazyLoadQuery<InternSidebarBrandsQuery>(
    brandsQuery,
    {}
  );
  const brands = brandsData.internBrands;

  // Brand selector menu state
  const [brandMenuAnchor, setBrandMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const brandMenuOpen = Boolean(brandMenuAnchor);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "true";
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  const handleNavClick = useCallback(
    (path: string) => {
      navigate(path);
      onMobileClose();
    },
    [navigate, onMobileClose]
  );

  const handleBrandSelect = useCallback(
    (brand: { id: string; displayName: string }) => {
      setSelectedBrand(brand);
      setBrandMenuAnchor(null);
      // Navigate to workspace overview when selecting a brand
      navigate("/intern/workspace/overview");
      onMobileClose();
    },
    [setSelectedBrand, navigate, onMobileClose]
  );

  const cycleColorMode = useCallback(() => {
    if (isSystemPreference) {
      setColorMode("light");
    } else if (colorMode === "light") {
      setColorMode("dark");
    } else {
      setColorMode("system");
    }
  }, [colorMode, isSystemPreference, setColorMode]);

  const getColorModeIcon = () => {
    if (isSystemPreference) return <SettingsBrightnessIcon />;
    if (colorMode === "dark") return <DarkModeIcon />;
    return <LightModeIcon />;
  };

  const getColorModeLabel = () => {
    if (isSystemPreference) return "System";
    if (colorMode === "dark") return "Dark";
    return "Light";
  };

  const sidebarWidth = collapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;

  // Section header component
  const SectionHeader = ({
    title,
    collapsed: isCollapsed,
  }: {
    title: string;
    collapsed: boolean;
  }) =>
    !isCollapsed ? (
      <Typography
        sx={{
          px: 2,
          py: 1,
          fontSize: "0.6875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--intern-sidebar-text-muted)",
          opacity: 0.6,
        }}
      >
        {title}
      </Typography>
    ) : null;

  // Brand selector component
  const BrandSelector = ({ isCollapsed }: { isCollapsed: boolean }) => (
    <>
      <Tooltip
        title={isCollapsed ? selectedBrandName || "Select Brand" : ""}
        placement="right"
      >
        <ListItemButton
          onClick={(e) => setBrandMenuAnchor(e.currentTarget)}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            mx: isCollapsed ? 0.5 : 1,
            px: isCollapsed ? 1.5 : 2,
            py: 1,
            minHeight: 40,
            justifyContent: isCollapsed ? "center" : "space-between",
            bgcolor: selectedBrandId
              ? "var(--intern-sidebar-active)"
              : "transparent",
            border: "1px dashed",
            borderColor: selectedBrandId
              ? "transparent"
              : "var(--intern-sidebar-text-muted)",
            "&:hover": {
              bgcolor: "var(--intern-sidebar-hover)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <ListItemIcon
              sx={{
                color: selectedBrandId
                  ? "var(--intern-sidebar-text)"
                  : "var(--intern-sidebar-text-muted)",
                minWidth: isCollapsed ? 0 : 36,
                justifyContent: "center",
              }}
            >
              <BusinessIcon />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary={selectedBrandName || "Select Brand"}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: selectedBrandId ? 600 : 400,
                  color: selectedBrandId
                    ? "var(--intern-sidebar-text)"
                    : "var(--intern-sidebar-text-muted)",
                  noWrap: true,
                }}
              />
            )}
          </Box>
          {!isCollapsed && (
            <ExpandMoreIcon
              sx={{
                color: "var(--intern-sidebar-text-muted)",
                fontSize: 18,
                ml: 0.5,
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={brandMenuAnchor}
        open={brandMenuOpen}
        onClose={() => setBrandMenuAnchor(null)}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 280,
            bgcolor: "var(--intern-bg-primary)",
            border: "1px solid var(--intern-border-default)",
          },
        }}
      >
        {brands.map((brand) => (
          <MenuItem
            key={brand.id}
            onClick={() => handleBrandSelect(brand)}
            selected={brand.id === selectedBrandId}
            sx={{
              fontSize: "0.875rem",
              "&.Mui-selected": {
                bgcolor: "var(--intern-bg-hover)",
              },
            }}
          >
            {brand.displayName}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  // Nav item renderer
  const renderNavItem = (item: NavItem, isCollapsed: boolean) => {
    const isActive = isNavItemActive(item, location.pathname);
    const isDisabled = item.disabled;

    return (
      <Tooltip
        key={item.path + item.name}
        title={isCollapsed ? item.name : ""}
        placement="right"
      >
        <ListItemButton
          onClick={() => !isDisabled && handleNavClick(item.path)}
          disabled={isDisabled}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            px: isCollapsed ? 1.5 : 2,
            py: 1,
            minHeight: 40,
            justifyContent: isCollapsed ? "center" : "flex-start",
            bgcolor: isActive ? "var(--intern-sidebar-active)" : "transparent",
            opacity: isDisabled ? 0.5 : 1,
            "&:hover": {
              bgcolor: isDisabled
                ? "transparent"
                : "var(--intern-sidebar-hover)",
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: isActive
                ? "var(--intern-sidebar-text)"
                : "var(--intern-sidebar-text-muted)",
              minWidth: isCollapsed ? 0 : 36,
              justifyContent: "center",
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: isActive ? 600 : 400,
                color: isActive
                  ? "var(--intern-sidebar-text)"
                  : "var(--intern-sidebar-text-muted)",
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  // Apple-style full-screen mobile menu content
  const mobileMenuContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        bgcolor:
          colorMode === "dark"
            ? "rgba(0, 0, 0, 0.95)"
            : "rgba(255, 255, 255, 0.98)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        overflow: "auto",
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minHeight: 56,
        }}
      >
        <IconButton
          onClick={onMobileClose}
          sx={{
            color: colorMode === "dark" ? "#fff" : "#000",
            width: 44,
            height: 44,
          }}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>

      {/* Navigation Sections */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: 3,
          py: 2,
        }}
      >
        {/* Platform Section */}
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color:
              colorMode === "dark"
                ? "rgba(255,255,255,0.5)"
                : "rgba(0,0,0,0.4)",
            mb: 2,
          }}
        >
          Platform
        </Typography>
        {PLATFORM_NAV_ITEMS.map((item, index) => {
          const isActive = isNavItemActive(item, location.pathname);
          return (
            <Fade
              key={item.path}
              in={mobileOpen}
              timeout={{ enter: 200 + index * 50, exit: 100 }}
            >
              <ButtonBase
                onClick={() => handleNavClick(item.path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  py: 1.5,
                  px: 2,
                  mb: 0.5,
                  borderRadius: 2,
                  width: "100%",
                  bgcolor: isActive
                    ? colorMode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)"
                    : "transparent",
                }}
              >
                <Box
                  sx={{
                    mr: 2,
                    color:
                      colorMode === "dark"
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.6)",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.125rem",
                    fontWeight: isActive ? 600 : 400,
                    color: colorMode === "dark" ? "#fff" : "#000",
                  }}
                >
                  {item.name}
                </Typography>
              </ButtonBase>
            </Fade>
          );
        })}

        <Divider
          sx={{
            my: 3,
            borderColor:
              colorMode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.08)",
          }}
        />

        {/* Brand Workspace Section */}
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color:
              colorMode === "dark"
                ? "rgba(255,255,255,0.5)"
                : "rgba(0,0,0,0.4)",
            mb: 2,
          }}
        >
          Brand Workspace
        </Typography>

        {/* Brand Selector - Mobile */}
        <ButtonBase
          onClick={(e) => setBrandMenuAnchor(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            mb: 2,
            borderRadius: 2,
            width: "100%",
            border: "1px solid",
            borderColor: selectedBrandId
              ? colorMode === "dark"
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.1)"
              : colorMode === "dark"
              ? "rgba(255,255,255,0.3)"
              : "rgba(0,0,0,0.2)",
            bgcolor: selectedBrandId
              ? colorMode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.02)"
              : "transparent",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <BusinessIcon
              sx={{
                mr: 2,
                color:
                  colorMode === "dark"
                    ? "rgba(255,255,255,0.7)"
                    : "rgba(0,0,0,0.6)",
              }}
            />
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: selectedBrandId ? 600 : 400,
                color: colorMode === "dark" ? "#fff" : "#000",
              }}
            >
              {selectedBrandName || "Select Brand"}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              color:
                colorMode === "dark"
                  ? "rgba(255,255,255,0.5)"
                  : "rgba(0,0,0,0.4)",
            }}
          />
        </ButtonBase>

        {/* Workspace Nav Items */}
        {WORKSPACE_NAV_ITEMS.map((item, index) => {
          const isActive = isNavItemActive(item, location.pathname);
          const isDisabled = !selectedBrandId;
          return (
            <Fade
              key={item.path}
              in={mobileOpen}
              timeout={{ enter: 300 + index * 50, exit: 100 }}
            >
              <ButtonBase
                onClick={() => !isDisabled && handleNavClick(item.path)}
                disabled={isDisabled}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  py: 1.5,
                  px: 2,
                  mb: 0.5,
                  borderRadius: 2,
                  width: "100%",
                  opacity: isDisabled ? 0.4 : 1,
                  bgcolor: isActive
                    ? colorMode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)"
                    : "transparent",
                }}
              >
                <Box
                  sx={{
                    mr: 2,
                    color:
                      colorMode === "dark"
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.6)",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.125rem",
                    fontWeight: isActive ? 600 : 400,
                    color: colorMode === "dark" ? "#fff" : "#000",
                  }}
                >
                  {item.name}
                </Typography>
              </ButtonBase>
            </Fade>
          );
        })}
      </Box>

      {/* Footer with theme toggle */}
      <Divider
        sx={{
          mx: 3,
          borderColor:
            colorMode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
        }}
      />
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <ButtonBase
          onClick={cycleColorMode}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.5,
            px: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor:
              colorMode === "dark"
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              color:
                colorMode === "dark"
                  ? "rgba(255,255,255,0.7)"
                  : "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {getColorModeIcon()}
          </Box>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 500,
              color:
                colorMode === "dark"
                  ? "rgba(255,255,255,0.7)"
                  : "rgba(0,0,0,0.6)",
            }}
          >
            {getColorModeLabel()}
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );

  // Desktop sidebar content (collapsible)
  const desktopSidebarContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "var(--intern-sidebar-bg)",
        color: "var(--intern-sidebar-text)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          minHeight: 56,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              "& svg path": {
                fill: "var(--intern-sidebar-text)",
              },
            }}
          >
            <KinternLogo height="24px" />
          </Box>
        )}
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              color: "var(--intern-sidebar-text-muted)",
              "&:hover": {
                color: "var(--intern-sidebar-text)",
                bgcolor: "var(--intern-sidebar-hover)",
              },
            }}
            size="small"
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
        {/* Platform Section */}
        <SectionHeader title="Platform" collapsed={collapsed} />
        <List sx={{ px: collapsed ? 0.5 : 1, pt: 0 }}>
          {PLATFORM_NAV_ITEMS.map((item) => renderNavItem(item, collapsed))}
        </List>

        {!collapsed && (
          <Divider
            sx={{
              my: 1.5,
              mx: 2,
              borderColor: "rgba(255, 255, 255, 0.08)",
            }}
          />
        )}

        {/* Brand Workspace Section */}
        <SectionHeader title="Brand Workspace" collapsed={collapsed} />
        <List sx={{ px: collapsed ? 0.5 : 1, pt: 0 }}>
          {/* Brand Selector */}
          <BrandSelector isCollapsed={collapsed} />

          {/* Workspace Nav Items */}
          {WORKSPACE_NAV_ITEMS.map((item) =>
            renderNavItem({ ...item, disabled: !selectedBrandId }, collapsed)
          )}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Tooltip
          title={collapsed ? `Theme: ${getColorModeLabel()}` : ""}
          placement="right"
        >
          <ListItemButton
            onClick={cycleColorMode}
            sx={{
              borderRadius: 1,
              px: collapsed ? 1.5 : 2,
              py: 1,
              justifyContent: collapsed ? "center" : "flex-start",
              "&:hover": {
                bgcolor: "var(--intern-sidebar-hover)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "var(--intern-sidebar-text-muted)",
                minWidth: collapsed ? 0 : 36,
                justifyContent: "center",
              }}
            >
              {getColorModeIcon()}
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={getColorModeLabel()}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  color: "var(--intern-sidebar-text-muted)",
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Apple-style Full-Screen Mobile Menu */}
      <Drawer
        anchor="top"
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            bgcolor: "transparent",
          },
          "& .MuiBackdrop-root": {
            bgcolor: "transparent",
          },
        }}
        transitionDuration={{ enter: 300, exit: 250 }}
      >
        {mobileMenuContent}
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        component="nav"
        sx={{
          display: { xs: "none", md: "block" },
          width: sidebarWidth,
          flexShrink: 0,
          transition: "width 0.2s ease-in-out",
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: sidebarWidth,
            transition: "width 0.2s ease-in-out",
            zIndex: theme.zIndex.drawer,
          }}
        >
          {desktopSidebarContent}
        </Box>
      </Box>
    </>
  );
};

export { SIDEBAR_EXPANDED_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default InternSidebar;
